import { useState, useEffect } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import {
  PageContainer, PremiumCard, StatCard, LoadingState, EmptyState, StatusBadge,
} from "components/ui";

const Icon = ({ d, size = 20, stroke = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IconBuilding = () => <Icon d={["M4 22V4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v18", "M9 22v-4h6v4", "M9 8h.01", "M15 8h.01", "M9 12h.01", "M15 12h.01"]} />;
const IconUsers    = () => <Icon d={["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2", "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M23 21v-2a4 4 0 0 0-3-3.87", "M16 3.13a4 4 0 0 1 0 7.75"]} />;
const IconBox      = () => <Icon d={["M21 8a1 1 0 0 0-.5-.87l-8-4.62a1 1 0 0 0-1 0l-8 4.62A1 1 0 0 0 3 8v8a1 1 0 0 0 .5.87l8 4.62a1 1 0 0 0 1 0l8-4.62A1 1 0 0 0 21 16z", "M3.27 6.96 12 12.01l8.73-5.05", "M12 22.08V12"]} />;
const IconCalendar = () => <Icon d={["M8 2v4", "M16 2v4", "M3 10h18", "M4 4h16a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"]} />;

function UsageBar({ used, max, label }) {
  const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0;
  const isFull = used >= max;
  const isNear = pct >= 80 && !isFull;

  const barColor = isFull ? "var(--danger)" : isNear ? "var(--warning)" : "var(--brand-primary)";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
        <span className="t-muted">{label}</span>
        <span style={{ fontWeight: 600, color: isFull ? "var(--danger)" : "var(--text-body)" }}>
          {used} / {max}
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 8, background: "var(--surface-sunken)", overflow: "hidden" }}>
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: barColor,
            borderRadius: 8,
            transition: "width 0.3s ease",
          }}
        />
      </div>
    </div>
  );
}

const TenantPlanUsage = () => {
  const axiosPrivate = useAxiosPrivate();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const res = await axiosPrivate.get("/api/tenant-admin/plan-usage");
        setUsage(res.data);
      } catch (err) {
        console.error(err);
        setError(true);
        toast.error("Failed to load plan usage");
      } finally {
        setLoading(false);
      }
    };
    fetchUsage();
  }, [axiosPrivate]);

  if (loading) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState label="Loading plan usage…" /></PremiumCard>
      </PageContainer>
    );
  }

  if (error || !usage) {
    return (
      <PageContainer>
        <PremiumCard>
          <EmptyState title="No active subscription" description="Contact support to activate a plan for your organization." />
        </PremiumCard>
      </PageContainer>
    );
  }

  const daysLeft = usage.daysUntilRenewal;
  const renewalTone = daysLeft != null && daysLeft <= 7 ? "var(--danger)" : "var(--text-body)";

  return (
    <PageContainer>
      {/* Plan summary strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <StatCard
          label="Current plan"
          value={usage.planName}
          icon={<IconBuilding />}
          hint={usage.planType}
        />
        <StatCard
          label="Price"
          value={`${usage.currency} ${Number(usage.pricePaid).toFixed(2)}`}
          icon={<IconCalendar />}
          hint={usage.billingCycle === "YEARLY" ? "per year" : "per month"}
        />
        <StatCard
          label="Renews in"
          value={daysLeft != null ? `${daysLeft}d` : "—"}
          icon={<IconCalendar />}
          accent={daysLeft != null && daysLeft <= 7 ? { bg: "var(--danger-bg)", fg: "var(--danger)" } : undefined}
          hint={usage.renewalDate || "not set"}
        />
        <StatCard
          label="Status"
          value={<StatusBadge value={usage.subscriptionStatus} dot />}
          icon={<IconBuilding />}
        />
      </div>

      {/* Branch limit */}
      <PremiumCard title="Organization usage" subtitle="Limits included in your current plan" className="mb-3">
        <div style={{ maxWidth: 420, marginBottom: 8 }}>
          <UsageBar used={usage.branchesUsed} max={usage.maxBranches} label="Branches" />
        </div>
      </PremiumCard>

      {/* Per-branch breakdown */}
      <PremiumCard title="Per-branch usage" subtitle="Users and products against your plan's per-branch limits">
        {usage.branchUsage.length === 0 ? (
          <EmptyState title="No branches yet" description="Create a branch to start using your plan." />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            {usage.branchUsage.map((b) => (
              <div
                key={b.branchId}
                style={{
                  border: "1px solid var(--border-subtle)",
                  borderRadius: 14,
                  padding: 18,
                  background: "var(--surface-muted)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
                  <IconBuilding /> {b.branchName}
                </div>
                <UsageBar used={b.usersUsed} max={b.maxUsersPerBranch} label="Users" />
                <UsageBar used={b.productsUsed} max={b.maxProductsPerBranch} label="Products" />
              </div>
            ))}
          </div>
        )}
      </PremiumCard>
    </PageContainer>
  );
};

export default TenantPlanUsage;